import { Alert } from "@mui/material";
import * as React from "react";

import * as schema from "@/shared/state/visualization/schema/v1/three-d";

/**
 * localStorage key. Presence (`"true"`) means the user has already
 * seen and dismissed the free-cam onboarding hint; absence means it
 * has not been shown yet.
 */
const STORAGE_KEY = "viz.threeDPanel.freeCameraHintSeen";

interface Props {
  /**
   * The active camera spec's `type` field. The hint fires once on the
   * orbit → third-person transition (the user toggled into free
   * camera mode), and never again — the localStorage flag persists
   * across reloads. A panel that loads directly into third-person
   * mode (no transition observed) does not fire the hint.
   */
  cameraType: schema.Camera["type"] | undefined;
  className?: string;
}

/**
 * One-time onboarding hint shown the first time the user toggles into
 * the third-person ("free camera") mode. Renders as an MUI `info`
 * Alert positioned by the caller via `className`. Auto-dismisses on
 * the next state transition or via the close button.
 */
export function FreeCamHintAlert({ cameraType, className }: Props) {
  const [visible, setVisible] = React.useState(false);
  const previousType = React.useRef(cameraType);

  React.useEffect(() => {
    const wasNotThirdPerson =
      previousType.current !== schema.CAMERA_TYPE.thirdPerson;
    const isNowThirdPerson = cameraType === schema.CAMERA_TYPE.thirdPerson;
    previousType.current = cameraType;
    if (!wasNotThirdPerson || !isNowThirdPerson) {
      return;
    }
    if (localStorage.getItem(STORAGE_KEY) === "true") {
      return;
    }
    setVisible(true);
    localStorage.setItem(STORAGE_KEY, "true");
  }, [cameraType]);

  if (!visible) {
    return null;
  }

  return (
    <Alert
      severity="info"
      onClose={() => setVisible(false)}
      className={className}
    >
      You are now in free camera mode. Camera tilt and zoom works the same as
      with the tracked camera. In addition, you can also pan (Shift + Drag) or
      move the camera vertically (Shift + Scroll).
    </Alert>
  );
}
